import NumberConverter from '../services/numberConverter';
import traversingService from '../services/traversingService';

/**
 * Erhält die Konfigration der abhängigen Felder von Gravity Forms und übernimmt das anzeigen/ausblenden von Feldern.
 *
 * Beispiel-Config
 *
 * config: {
 *  actionType: "show"
 *  logicType: "any"
 *  rules: [
 *      {
 *          fieldId: "6"
 *          operator: "is"
 *          value: "Prüfung des Anforderungsdokument" // Beispielwert aus einem Select
 *      },
 *      ...
 *  ]
 * }
 */
class ConditionalLogic {
    constructor(fields) {
        this.selectors = '';
        this.conditionalFields = [];
        this.dataCollection = [];
        this.formLogicChangedEvent = null;
        this.window = window;
        this.formId = 0;

        this.fields = fields;
        this.fieldSelectors = [
            'input[id="input_{0}_{1}"]',
            'input[id^="input_{0}_{1}_"]',
            'input[id="choice_{0}_{1}"]',
            'input[id^="choice_{0}_{1}_"]',
            'select#input_{0}_{1}',
            'textarea#input_{0}_{1}',
        ];

        if (!this.fields) return;

        this.prepareDependentFields()
            .then(() => {
                this.init();
            })
            .catch((error) => {
                throw error;
            });
    }

    set fieldSelectors(selectors) {
        this.selectors = selectors.join();
    }

    get fieldSelectors() {
        return this.selectors;
    }

    set fields(fields) {
        fields.forEach((field) => {
            const {conditionalLogic} = field.dataset;
            let conditions = {};
            this.formId = field.dataset.formId;

            if (!conditionalLogic) throw new Error(`No conditional logic found: ${field}`);

            try {
                conditions = JSON.parse(conditionalLogic);
            } catch (e) {
                throw new Error(`Cannot parse json: ${e}`);
            }

            conditions.actionType = (conditions.actionType === 'show') ? 'block' : 'none';

            this.conditionalFields.push({
                input: field,
                conditions,
            });
        });
    }

    get fields() {
        return this.conditionalFields;
    }

    prepareDependentFields() {
        return new Promise((resolve, reject) => {
            this.fields.forEach((field) => {
                const {rules} = field.conditions;
                const triggers = [];

                if (!rules.length) reject(new Error(`No rules found: ${rules}`));

                rules.forEach((rule) => {
                    const selector = ConditionalLogic.format(this.fieldSelectors, this.formId, rule.fieldId);
                    const triggerElements = Array.from(document.querySelectorAll(selector));
                    triggerElements.forEach((triggerElement) => {
                        if (!triggers.includes(triggerElement)) {
                            triggers.push(triggerElement);
                        }
                    });
                });

                this.dataCollection.push({
                    triggers,
                    field,
                    rules,
                });
            });

            resolve();
        });
    }

    init() {
        this.registerCustomEvents();
        this.dataCollection.forEach((entry) => {
            entry.triggers.forEach((trigger) => {
                this.checkConditionalFields(entry);
                this.registerEvent(trigger, entry);
            });
        });
    }

    registerCustomEvents() {
        if (!this.formLogicChangedEvent) {
            this.formLogicChangedEvent = document.createEvent('HTMLEvents');
            this.formLogicChangedEvent.initEvent('formLogicChanged', true, true);
            this.formLogicChangedEvent.eventName = 'formLogicChanged';
        }
    }

    registerEvent(trigger, entry) {
        if (!(trigger instanceof HTMLElement)) return;

        let event = 'input';
        if (trigger.options || ['radio', 'checkbox'].includes(trigger.type)) {
            event = 'change';
        }
        trigger.addEventListener(event, () => this.checkConditionalFields(entry));
    }

    checkConditionalFields(entry) {
        const {input, conditions} = entry.field;
        const isRequired = input.dataset.required;
        const {rules} = entry;
        const shouldShow = conditions.actionType === 'block';
        const inputParent = traversingService.getParentByClass(input, 'js-conditional-field-parent');
        const displayContainer = inputParent || input;
        const matches = ConditionalLogic.isMatch(entry, rules);

        if (ConditionalLogic.canAction(conditions.logicType, matches, rules)) {
            displayContainer.style.display = conditions.actionType;
            input.disabled = !shouldShow;
        } else {
            displayContainer.style.display = conditions.actionType === 'block' ? 'none' : 'block';
            input.disabled = shouldShow;
        }

        if (input.disabled) {
            input.removeAttribute('required');
        } else if (!input.disabled && isRequired) {
            input.setAttribute('required', 'required');
        }

        this.window.dispatchEvent(this.formLogicChangedEvent);
    }

    static isMatch(entry, rules) {
        let matches = 0;

        if (!entry.field.input || !entry.field.input.dataset) {
            return matches;
        }

        const {formId} = entry.field.input.dataset;
        entry.triggers.forEach((trigger) => {
            if (!trigger.dataset || trigger.dataset.formId !== formId) {
                return;
            }

            const isCheckable = ['checkbox', 'radio'].includes(trigger.type) && !trigger.checked;
            const isSelectable = (trigger.options) ? trigger.options.length : false;
            const selectedOption = (isSelectable) ? trigger.options[trigger.selectedIndex].value : null;
            let {value} = trigger;

            if (isCheckable) {
                value = '';
            }

            if (isSelectable) {
                value = selectedOption;
            }

            rules.forEach((rule) => {
                if (trigger.name && rule.fieldId === trigger.name.split('.')[0].replace('input_', '')
                    && ConditionalLogic.matchesOperation(value, rule.value, rule.operator)) {
                    matches += 1;
                }
            });
        });

        return matches;
    }

    static canAction(logicType, matches, rules) {
        return (logicType === 'all' && matches === rules.length)
            || (logicType === 'any' && matches > 0);
    }

    static matchesOperation(fieldValue, ruleValue, operation) {
        let val1 = fieldValue ? fieldValue.toLowerCase() : '';
        let val2 = ruleValue ? ruleValue.toLowerCase() : '';
        let start = -1;
        let tail = null;
        let result = false;

        switch (operation) {
            case 'is':
                result = val1 === val2;
                break;
            case 'isnot':
                result = val1 !== val2;
                break;
            case '>':
                val1 = NumberConverter.tryConvertFloat(val1);
                val2 = NumberConverter.tryConvertFloat(val2);

                result = NumberConverter.isNumber(val2) ? val1 > val2 : false;
                break;
            case '<':
                val1 = NumberConverter.tryConvertFloat(val1);
                val2 = NumberConverter.tryConvertFloat(val2);

                result = NumberConverter.isNumber(val1) && NumberConverter.isNumber(val2) ? val1 < val2 : false;
                break;
            case 'contains':
                result = val1.indexOf(val2) >= 0;
                break;
            case 'starts_with':
                result = val1.indexOf(val2) === 0;
                break;
            case 'ends_with':
                start = val1.length - val2.length;

                if (start < 0) return false;

                tail = val1.substring(start);
                result = val2 === tail;
                break;
            default:
                break;
        }

        return result;
    }

    static format(string, ...args) {
        return string.replace(/{(\d+)}/g, (match, number) => {
            if (typeof args[number] !== 'undefined') {
                return args[number];
            }

            return match;
        });
    }
}

const conditionalLogic = {
    init() {
        const fields = document.querySelectorAll('.js-conditional-field');

        if (!fields) return;

        this.logic = new ConditionalLogic(fields);
    },
};

export default conditionalLogic;
